/**
 * Age Gate Component
 * Handles age verification modal functionality
 */

class AgeGate {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.confirmButton = container.querySelector('[data-age-confirm]');
    this.declineButton = container.querySelector('[data-age-decline]');
    this.storageKey = 'age_gate_verified';
    this.minAge = 21;
    this.isActive = false;
    
    this.init();
  }
  
  init() {
    // Check if user has already verified age
    if (this.isAgeVerified()) {
      return;
    }
    
    // Show age gate
    this.show();
    
    // Add event listeners
    this.bindEvents();
    
    // Prevent closing with escape key
    this.preventEscapeKey();
  }
  
  bindEvents() {
    if (this.confirmButton) {
      this.confirmButton.addEventListener('click', () => this.confirmAge());
    }
    
    if (this.declineButton) {
      this.declineButton.addEventListener('click', () => this.declineAge());
    }
    
    // Add keyboard navigation
    this.container.addEventListener('keydown', (e) => this.handleKeydown(e));
    
    // Focus management
    this.container.addEventListener('focusin', (e) => this.handleFocusIn(e));
  }
  
  handleKeydown(e) {
    // Allow tab navigation within the modal
    if (e.key === 'Tab') {
      const focusableElements = this.container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }
  
  handleFocusIn(e) {
    // Keep focus within the modal
    if (!this.container.contains(e.target)) {
      this.confirmButton?.focus();
    }
  }
  
  preventEscapeKey() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isActive) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }
  
  isAgeVerified() {
    try {
      return localStorage.getItem(this.storageKey) === 'true';
    } catch (error) {
      console.warn('Age gate: Could not access localStorage:', error);
      return false;
    }
  }
  
  show() {
    this.container.classList.add('is-active');
    document.body.classList.add('age-gate-active');
    this.isActive = true;
    
    // Focus the confirm button for accessibility
    setTimeout(() => {
      this.confirmButton?.focus();
    }, 100);
    
    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('ageGateShown'));
  }
  
  hide() {
    this.container.classList.remove('is-active');
    document.body.classList.remove('age-gate-active');
    this.isActive = false;
    
    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('ageGateHidden'));
  }
  
  confirmAge() {
    try {
      localStorage.setItem(this.storageKey, 'true');
    } catch (error) {
      console.warn('Age gate: Could not save to localStorage:', error);
    }
    
    this.hide();
    
    const verificationMethod =
      this.container.dataset.verificationMethod || 'yes_no_button';

    document.dispatchEvent(
      new CustomEvent('ageGateConfirmed', {
        detail: { verification_method: verificationMethod },
      })
    );
  }
  
  declineAge() {
    // Get decline URL from section settings or use default
    const declineUrl = this.container.dataset.declineUrl || 'https://www.google.com';
    
    // Dispatch custom event before redirect
    document.dispatchEvent(new CustomEvent('ageGateDeclined'));
    
    // Redirect to safe page
    window.location.href = declineUrl;
  }
  
  // Public method to reset age verification (useful for testing)
  reset() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn('Age gate: Could not remove from localStorage:', error);
    }
  }
  
  // Public method to manually show age gate
  showManually() {
    if (!this.isAgeVerified()) {
      this.show();
    }
  }
}

// Initialize age gate when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const ageGateContainer = document.querySelector('[data-section-type="age-gate"]');
  if (ageGateContainer) {
    window.ageGate = new AgeGate(ageGateContainer);
  }
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AgeGate;
} 